<template>
    <div>
        <b-navbar id="nav" class="nav-root" :class="{ hide: value }" toggleable="md">
            <div
                id="nav-image-wrapper"
                class="image-wrapper"
                :class="{
                    pointer: !onRecoverPage,
                }"
                @click="!onRecoverPage ? goToHomePage() : undefined"
            >
                <img :src="logo" alt="School logo" />
            </div>

            <!-- Content -->
            <div class="d-none d-md-flex w-100 align-items-center">
                <div v-if="!onRecoverPage" class="mx-3">
                    <div class="d-flex">
                        <NavLink
                            v-if="hasMyPage"
                            class="action-item"
                            :to="mypageURL()"
                            :label="$t('components.navigation.navigation.myPage')"
                        />
                        <NavLink
                            v-else-if="hasMyProfile"
                            class="action-item"
                            :to="myProfileURL()"
                            :label="$t('shared.userNavSection.myProfile')"
                            :disabled="!isAuthenticated()"
                        />
                        <NavLink
                            v-if="isCompanyPlatform"
                            :exact="true"
                            class="action-item"
                            to="/"
                            :label="$t('components.navigation.navigation.vfairs')"
                        />
                        <NavLink
                            v-if="showJobs"
                            class="action-item"
                            :to="findJobsURL()"
                            :label="$t('components.navigation.navigation.jobs')"
                            exact
                        />
                        <NavLink
                            v-if="showCompanies"
                            class="action-item"
                            :to="findCompaniesURL()"
                            :label="$t('components.navigation.navigation.companies')"
                        />
                        <NavLink
                            v-if="hasMasterClasses"
                            class="action-item"
                            :to="findResourcesURL()"
                            :label="$t('components.navigation.navigation.resources')"
                            exact
                        />
                        <NavLink
                            v-if="hasAssessments"
                            class="action-item"
                            :to="findCuteURL()"
                            :label="$t('components.navigation.navigation.assessments')"
                        />
                        <NavLink
                            v-if="hasCountryGuides"
                            class="action-item"
                            :to="findCountryGuideURL()"
                            :external="true"
                            :newTab="true"
                            :label="$t('components.navigation.navigation.countryGuides')"
                        />
                        <NavLink
                            v-if="showHaiguiLink()"
                            class="action-item"
                            :to="findHaiguiURL()"
                            :external="true"
                            :newTab="true"
                            :label="$t('components.navigation.navigation.goToHaigui')"
                        />
                        <NavLink
                            v-if="hasVfairs"
                            class="action-item"
                            :to="findVfairs()"
                            :label="$t('components.navigation.navigation.vfairs')"
                        />
                    </div>
                </div>

                <!-- Things on the right -->
                <div class="ml-auto">
                    <div v-if="!isAuthenticated()" class="d-flex align-items-center">
                        <LocaleDropdown class="ml-2" />
                        <BaseButton
                            v-if="!onRecoverPage && hasLogin"
                            class="ml-2"
                            variant="primary"
                            @click="openLoginModule"
                            >{{ $t('components.navigation.navigation.signIn') }}</BaseButton
                        >
                    </div>

                    <div v-else class="d-flex align-items-center">
                        <UniversityBadges
                            v-if="showProfileBanner && showUniversityBadges"
                            :key="1"
                            class="mr-1"
                        />
                        <LocaleDropdown class="mr-1" />
                        <Avatar
                            v-if="showProfileBanner"
                            class="pointer"
                            :image="userData().image"
                            @click.native="enableProfileNav"
                        >
                            {{ firstLetterInName }}
                            <BaseIcon
                                slot="badge"
                                :name="showProfileNav ? 'chevron-up' : 'chevron-down'"
                                class="white"
                                size="xs"
                            />
                        </Avatar>
                        <transition name="vertical-slide" mode="out-in">
                            <ProfileNavigation
                                v-if="showProfileNav"
                                :nameAbbr="firstLetterInName"
                                @close="toggleProfileNav"
                            />
                        </transition>
                    </div>
                </div>
            </div>

            <!-- Menu Icon -->
            <div class="d-md-none d-flex align-items-center">
                <UniversityBadges
                    v-if="isAuthenticated() && showProfileBanner && showUniversityBadges"
                    :key="2"
                    class="mr-1"
                />
                <IconBtn class="primary-color" name="bars" size="lg" @click="toggleSidebar" />
            </div>
        </b-navbar>
    </div>
</template>

<script>
// Component specific imports
import LOGO from '../../config/LOGO.png';
import NavLink from './NavLink';
import ProfileNavigation from './ProfileNavigation';
import LocaleDropdown from './LocaleDropdown';

// Utils
import URLS from '~/URLS';
import UniversityService from '~/services/UniversityService';

import {
    SHOW_JOBS,
    SHOW_COMPANIES,
    HAS_MY_PAGE,
    HAS_MY_PROFILE,
    HAS_ASSESSMENTS,
    HAS_MASTERCLASSES,
    HAS_COUNTRY_GUIDES,
    HAS_HAIGUI_LINK,
    HAS_LOGIN,
    HAS_VFAIRS,
    SHOW_PROFILE_BANNER,
    SHOW_UNIVERSITY_BADGES,
} from '~/router';

// External Components
import UniversityBadges from '~/shared/universitybadges/UniversityBadges';
import localization from '../../localization';

export default {
    name: 'Navigation',
    components: {
        NavLink,
        ProfileNavigation,
        UniversityBadges,
        LocaleDropdown,
    },
    props: {
        value: Boolean, // Show sidebar
    },
    data: () => ({
        logo: LOGO,
        showProfileNav: false,

        hasMyPage: HAS_MY_PAGE,
        hasMyProfile: HAS_MY_PROFILE,
        showJobs: SHOW_JOBS,
        showCompanies: SHOW_COMPANIES,
        hasAssessments: HAS_ASSESSMENTS,
        hasMasterClasses: HAS_MASTERCLASSES,
        hasCountryGuides: HAS_COUNTRY_GUIDES,
        hasHaiguiLink: HAS_HAIGUI_LINK,

        hasLogin: HAS_LOGIN,

        hasVfairs: false,
        showProfileBanner: SHOW_PROFILE_BANNER,
        showUniversityBadges: SHOW_UNIVERSITY_BADGES,
        isCompanyPlatform: localization.isCompanyPlatform,
    }),
    computed: {
        user() {
            return this.$interfaces.user.me.get({ reactive: true });
        },
        firstLetterInName() {
            return (this.user.data || {}).firstLetterInName;
        },
        onRecoverPage() {
            return this.$route.path === URLS.recoverUser;
        },
    },
    methods: {
        userData() {
            return this.user.data;
        },
        openLoginModule() {
            this.$interfaces.logInModule.resetDisabledPopup();
            this.$interfaces.logInModule.toggleOpen();
        },
        toggleSidebar() {
            this.$emit('input', !this.$props.value);
        },
        isAuthenticated() {
            return this.$interfaces.logInModule.isLoggedIn();
        },
        toggleProfileNav() {
            this.showProfileNav = !this.showProfileNav;
        },
        enableProfileNav() {
            if (!this.showProfileNav) {
                this.toggleProfileNav();
            }
        },
        mypageURL() {
            return this.$URLS.mypage;
        },
        myProfileURL() {
            return this.$URLS.spotlight;
        },
        findJobsURL() {
            return this.$URLS.findjobs;
        },
        findResourcesURL() {
            return this.$URLS.resources;
        },
        findCompaniesURL() {
            return this.$URLS.findcompanies;
        },
        findCuteURL() {
            return this.$URLS.tests.concat('/', this.$URLS.cute);
        },
        findCountryGuideURL() {
            return 'https://highered.global/wiki/index.php/Main_Page';
        },
        findHaiguiURL() {
            return 'https://haigui.gethighered.global';
        },
        findVfairs() {
            return this.$URLS.vfairs;
        },
        goToHomePage() {
            this.$scrollTo({ y: 0 });
            this.$interfaces.fakeLoad.startFindJobsFakeLoad();
            this.$router.push(this.findJobsURL());
        },
        showHaiguiLink() {
            if (!HAS_HAIGUI_LINK) {
                return false;
            }
            /*
            if (I18nService.getLocale() === 'cn') {
                return true;
            }
            let userLanguage =
                (navigator.languages && navigator.languages[0]) || // Chrome / Firefox
                navigator.language || // All browsers
                navigator.userLanguage; // IE <= 10

            return userLanguage.includes('zh');
            */
            return true;
        },
        initializeCMSSecret() {
            // Easter Egg functionality. On Ctrl-Alt-C => Open HigheredCMS
            window.addEventListener('keydown', async event => {
                if (event.altKey && event.ctrlKey && event.keyCode === 67) {
                    const school = await UniversityService.getCurrentSchoolData();
                    if (school !== null) {
                        window.open(`https://higheredcms.com/edit-school/${school.id}`);
                    }
                }
            });
        },
    },
    mounted() {
        this.initializeCMSSecret();

        if (localization.isSchoolPlatform) {
            this.$interfaces.vfairModule.setHasVFairs(HAS_VFAIRS);
            this.hasVfairs = HAS_VFAIRS;
        }
    },
};
</script>

<style scoped>
.nav-root {
    padding: 0 30px;
    width: 100%;
    height: 70px;
    background-color: white;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 2;

    box-shadow: 0px 0px 14px 10px rgba(17, 17, 17, 0.07);

    transition: height 0.25s linear;
    -webkit-transition: height 0.25s linear;
    -moz-transition: height 0.25s linear;
    -o-transition: height 0.25s linear;
}
.shrink {
    height: 60px;
}

.hide {
    z-index: -1;
}

.image-wrapper {
    height: 50px;
    max-width: 125px !important;

    transition: height 0.25s linear;
    -webkit-transition: height 0.25s linear;
    -moz-transition: height 0.25s linear;
    -o-transition: height 0.25s linear;
}
.image-wrapper > img {
    height: 100%;
    max-height: 100%;
    max-width: 100%;
    object-fit: scale-down;
}
.image-wrapper-shrink {
    height: 40px !important;
    max-width: 100px !important;
}

.action-item {
    padding: 0.5rem 0.5rem;
}
.activeClass {
    color: var(--primary);
}

@media only screen and (max-width: 1000px) {
    .nav-root {
        height: 60px !important;
        padding: 0 18px;
    }
    .image-wrapper {
        height: 40px !important;
        max-width: 100px !important;
    }
}
</style>
